import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";

const testimonials = [
  {
    name: "Carolina M.",
    role: "Bajó 15 kg en 6 meses",
    content: "Después de años pagando gimnasios donde nadie me miraba, encontré Inspirion. Por primera vez siento que realmente me acompañan y los resultados se notan.",
    rating: 5,
  },
  {
    name: "Roberto S.",
    role: "Ganó masa muscular",
    content: "El entrenamiento personalizado hizo toda la diferencia. Mi entrenador ajusta cada semana según mi progreso. En 4 meses logré lo que no pude en 2 años solo.",
    rating: 5,
  },
  {
    name: "Francisca L.",
    role: "Retomó el ejercicio",
    content: "Llevaba 5 años sin hacer ejercicio y tenía miedo de empezar. Aquí me recibieron sin juzgar y me ayudaron a recuperar mi condición física paso a paso.",
    rating: 5,
  },
  {
    name: "Miguel A.",
    role: "Tonificación y energía",
    content: "Lo mejor de Inspirion es el ambiente. No es el típico gym intimidante. Te motivan, te corrigen y celebran cada logro contigo.",
    rating: 5,
  },
];

export const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9,
    }),
  };

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setCurrentIndex((prev) => {
      if (newDirection === 1) {
        return (prev + 1) % testimonials.length;
      }
      return prev === 0 ? testimonials.length - 1 : prev - 1;
    });
  };

  return (
    <section 
      id="testimonios"
      data-testid="testimonials-section"
      className="relative py-24 bg-black overflow-hidden"
    >
      {/* Animated background */}
      <motion.div 
        className="absolute inset-0 opacity-5"
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%"],
        }}
        transition={{ duration: 30, repeat: Infinity, repeatType: "reverse" }}
        style={{
          backgroundImage: `linear-gradient(45deg, #00FF00 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-left mb-16"
        >
          <span className="text-neon-green text-sm font-bold uppercase tracking-widest">
            Testimonios
          </span>
          <h2 className="mt-4 text-4xl md:text-5xl font-black uppercase tracking-tight text-white">
            Lo que dicen
            <br />
            <span className="text-neon-magenta text-glow-magenta">nuestros alumnos</span>
          </h2>
        </motion.div>

        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-2 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 40px rgba(0, 255, 0, 0.1)"
              }}
              className="group relative"
            >
              <div className="h-full p-8 bg-dark-card border border-white/5 hover:border-neon-green/30 transition-all duration-500">
                {/* Quote icon */}
                <motion.div 
                  className="absolute top-6 right-6 opacity-20 group-hover:opacity-50 transition-opacity"
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <Quote className="w-12 h-12 text-neon-green" />
                </motion.div>

                {/* Rating with animation */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + i * 0.1 }}
                    >
                      <Star className="w-4 h-4 text-neon-green fill-neon-green" />
                    </motion.div>
                  ))}
                </div>

                {/* Content */}
                <p className="text-gray-300 leading-relaxed mb-6 relative z-10">
                  "{testimonial.content}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <motion.div 
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-neon-green/20 to-neon-cyan/20 flex items-center justify-center border border-neon-green/30"
                    whileHover={{ scale: 1.1, rotate: 10 }}
                  >
                    <span className="text-neon-green font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </span>
                  </motion.div>
                  <div>
                    <div className="font-bold text-white">{testimonial.name}</div>
                    <div className="text-sm text-neon-cyan">{testimonial.role}</div>
                  </div>
                </div>

                {/* Hover accent line */}
                <motion.div 
                  className="absolute bottom-0 left-0 h-1 bg-neon-green"
                  initial={{ width: 0 }}
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          <div className="overflow-hidden">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                className="w-full"
              >
                <div className="p-6 bg-dark-card border border-white/10">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-neon-green fill-neon-green" />
                    ))}
                  </div>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    "{testimonials[currentIndex].content}"
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-neon-green/20 to-neon-cyan/20 flex items-center justify-center border border-neon-green/30">
                      <span className="text-neon-green font-bold text-lg">
                        {testimonials[currentIndex].name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="font-bold text-white">{testimonials[currentIndex].name}</div>
                      <div className="text-sm text-neon-cyan">{testimonials[currentIndex].role}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex justify-center items-center gap-4 mt-6">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => paginate(-1)}
              className="p-2 border border-white/20 hover:border-neon-green/50 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </motion.button>
            
            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => {
                    setDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex ? "bg-neon-green w-6" : "bg-white/30"
                  }`}
                  whileHover={{ scale: 1.2 }}
                />
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => paginate(1)}
              className="p-2 border border-white/20 hover:border-neon-green/50 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
